import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronRight } from "lucide-react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { cn } from "../../lib/cn";
/**
 * A menu of actions, on the popover surface.
 *
 * A menu is not a select. Items here DO something; a select's items are values
 * you are choosing between. Using the wrong one is a real accessibility bug,
 * because the roles differ and screen readers say so.
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const menuSurface = [
    "lg:z-50 lg:min-w-44 lg:overflow-hidden lg:p-1",
    "lg:rounded-md lg:border lg:border-hairline-2 lg:bg-popover lg:text-popover-foreground",
    "lg:shadow-float",
    "lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out",
    "lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0",
    "lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95",
    "lg:data-[side=bottom]:slide-in-from-top-2 lg:data-[side=top]:slide-in-from-bottom-2",
    "lg:data-[side=left]:slide-in-from-right-2 lg:data-[side=right]:slide-in-from-left-2",
];
const menuItem = [
    "lg:relative lg:flex lg:cursor-pointer lg:select-none lg:items-center lg:gap-2",
    "lg:rounded-sm lg:px-3 lg:py-2 lg:text-body-sm lg:outline-none",
    "lg:transition-colors lg:focus:bg-secondary lg:focus:text-strong",
    "lg:data-[disabled]:pointer-events-none lg:data-[disabled]:opacity-50",
    "lg:[&_svg]:size-4 lg:[&_svg]:shrink-0",
];
export function DropdownMenuContent({ className, sideOffset = 6, ...props }) {
    return (_jsx(DropdownMenuPrimitive.Portal, { children: _jsx(DropdownMenuPrimitive.Content, { "data-slot": "dropdown-menu-content", sideOffset: sideOffset, className: cn(menuSurface, className), ...props }) }));
}
export function DropdownMenuItem({ className, destructive, ...props }) {
    return (_jsx(DropdownMenuPrimitive.Item, { "data-slot": "dropdown-menu-item", className: cn(menuItem, destructive === true &&
            "lg:text-destructive lg:focus:bg-destructive/10 lg:focus:text-destructive", className), ...props }));
}
export function DropdownMenuCheckboxItem({ className, children, ...props }) {
    return (_jsxs(DropdownMenuPrimitive.CheckboxItem, { "data-slot": "dropdown-menu-checkbox-item", className: cn(menuItem, "lg:pl-8", className), ...props, children: [_jsx("span", { className: "lg:absolute lg:left-2.5 lg:flex lg:size-3.5 lg:items-center lg:justify-center", children: _jsx(DropdownMenuPrimitive.ItemIndicator, { children: _jsx(Check, { className: "lg:size-3.5 lg:text-primary", "aria-hidden": "true" }) }) }), children] }));
}
export function DropdownMenuRadioItem({ className, children, ...props }) {
    return (_jsxs(DropdownMenuPrimitive.RadioItem, { "data-slot": "dropdown-menu-radio-item", className: cn(menuItem, "lg:pl-8", className), ...props, children: [_jsx("span", { className: "lg:absolute lg:left-2.5 lg:flex lg:size-3.5 lg:items-center lg:justify-center", children: _jsx(DropdownMenuPrimitive.ItemIndicator, { children: _jsx("span", { className: "lg:size-2 lg:rounded-full lg:bg-primary" }) }) }), children] }));
}
export function DropdownMenuLabel({ className, ...props }) {
    return (_jsx(DropdownMenuPrimitive.Label, { "data-slot": "dropdown-menu-label", className: cn("lg:px-3 lg:py-2 lg:font-label lg:text-label lg:uppercase lg:text-subtle", className), ...props }));
}
export function DropdownMenuSeparator({ className, ...props }) {
    return (_jsx(DropdownMenuPrimitive.Separator, { "data-slot": "dropdown-menu-separator", className: cn("lg:-mx-1 lg:my-1 lg:h-px lg:bg-hairline", className), ...props }));
}
export function DropdownMenuShortcut({ className, ...props }) {
    return (_jsx("span", { "data-slot": "dropdown-menu-shortcut", className: cn("lg:ml-auto lg:font-mono lg:text-micro lg:text-subtle", className), ...props }));
}
export function DropdownMenuSubTrigger({ className, children, ...props }) {
    return (_jsxs(DropdownMenuPrimitive.SubTrigger, { "data-slot": "dropdown-menu-sub-trigger", className: cn(menuItem, "lg:data-[state=open]:bg-secondary", className), ...props, children: [children, _jsx(ChevronRight, { className: "lg:ml-auto lg:size-4", "aria-hidden": "true" })] }));
}
export function DropdownMenuSubContent({ className, ...props }) {
    return (_jsx(DropdownMenuPrimitive.Portal, { children: _jsx(DropdownMenuPrimitive.SubContent, { "data-slot": "dropdown-menu-sub-content", className: cn(menuSurface, className), ...props }) }));
}
//# sourceMappingURL=dropdown-menu.js.map